import { z } from 'zod';
import { APPROVAL_STATUSES } from '../domain/approval.js';
import { BOARD_BUCKET_IDS } from '../domain/buckets.js';
import { TSHIRT_SIZES } from '../domain/estimation.js';
import { toSchemaResult } from './github.js';
/**
 * The portal's own API surface.
 *
 * These are USER-INPUT boundaries, the mirror image of `github.ts`: every object is STRICT,
 * so an unknown key is rejected rather than ignored. A typo'd field name must fail loudly
 * at the edge instead of silently doing nothing, and an unexpected key is how mass-assignment
 * gets attempted. Lengths are bounded so a request cannot be used to fill a column.
 */
/** Opaque identifiers: better-auth ids, bigint-as-string issue ids, uuids. */
const Identifier = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[A-Za-z0-9_-]+$/, 'must be an identifier');
export const HOURS_CEILING = 2000;
const Hours = z.number().min(0).max(HOURS_CEILING);
export const IssueIntakeSchema = z.strictObject({
    projectId: Identifier,
    title: z.string().trim().min(1).max(200),
    body: z.string().trim().max(20000).optional(),
});
/**
 * `pending` is absent by design: a decision endpoint resolves an approval, it never
 * un-resolves one. See `resolveApproval` — the audit log is append-only.
 */
export const ApprovalDecisionSchema = z.strictObject({
    issueId: Identifier,
    decision: z.enum(['approved', 'not_approved']),
    reason: z.string().trim().max(1000).optional(),
});
/**
 * How many approvals one click may commit to.
 *
 * Stephen described going through "like 50 features"; 100 leaves headroom and BOUNDS the
 * request — an unbounded array of ids is a request that can hold connections open for as long
 * as somebody cares to make it, and it is also the length of the audit loop and of the issue
 * list in the Slack message.
 */
export const BULK_APPROVAL_LIMIT = 100;
/**
 * `POST /api/issues/approvals` — decide many gated requests in one click.
 *
 * The ids are in the BODY rather than the URL, which does not make this any less a by-id
 * route: `cross-tenant.test.ts` carries an entry for it for exactly that reason.
 *
 * `status` reuses `ApprovalDecisionSchema.shape.decision` rather than restating a narrower
 * enum. No UI reaches the declining branch — Stephen asked to approve some and "leave it there"
 * for the rest, and leaving it there is doing nothing — but a second definition of the decision
 * vocabulary is worse than one unexercised branch.
 */
export const BulkApprovalSchema = z.strictObject({
    /*
     * DEDUPLICATED HERE, at the boundary, rather than trusted from the caller.
     *
     * The bound above caps the array, not the work: a hundred copies of one id was a hundred
     * reads and a hundred compare-and-set writes for a single row, and a response claiming one
     * decision against ninety-nine "already decided" — which is true, by this same request, and
     * unreadable. A `Set` is what the route wanted in the first place; every downstream count
     * is over rows, so making it one here means nothing further has to remember.
     */
    issueIds: z
        .array(Identifier)
        .min(1)
        .max(BULK_APPROVAL_LIMIT)
        .transform((ids) => [...new Set(ids)]),
    status: ApprovalDecisionSchema.shape.decision,
    reason: ApprovalDecisionSchema.shape.reason,
});
/**
 * Staff raising "we need your input on this" against work already underway.
 *
 * The note is bounded HERE, at the edge, and carries no CHECK constraint in the database —
 * persisted-data boundaries stay permissive, and this is the same treatment `CommentBodySchema`
 * gives free text. The wire shape the route actually parses is a discriminated union on
 * `action` (see `routes/parse.ts`); this is where its field bounds are defined once.
 */
export const AttentionSchema = z.strictObject({
    issueId: Identifier,
    note: z.string().trim().min(1).max(2000),
});
export const SizingSchema = z.strictObject({
    issueId: Identifier,
    size: z.enum(TSHIRT_SIZES),
    /** Optional override of the size default. Floored by `applyMinimum` server-side, not here. */
    estimatedHours: Hours.optional(),
});
export const ActualHoursSchema = z.strictObject({
    issueId: Identifier,
    actualHours: Hours,
    /** Delivered but deliberately not billed. */
    noCharge: z.boolean().optional(),
});
export const ProjectCreateSchema = z.strictObject({
    name: z.string().trim().min(1).max(100),
    slug: z
        .string()
        .trim()
        .min(1)
        .max(64)
        .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'must be lowercase words separated by single hyphens'),
    githubOwner: z
        .string()
        .trim()
        .min(1)
        .max(39)
        .regex(/^[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?$/, 'must be a GitHub owner'),
    githubRepo: z
        .string()
        .trim()
        .min(1)
        .max(100)
        .regex(/^[A-Za-z0-9._-]+$/, 'must be a GitHub repository name'),
    githubInstallationId: Identifier.optional(),
});
/**
 * Editing a project. Every field optional, but at least one required — an empty patch is a
 * request that means nothing, and answering it 200 teaches a caller their edit was applied.
 *
 * `archived` is here rather than on its own endpoint because retiring a project is a state
 * this form owns, but it is a separate field from the rest on purpose: it is never inferred
 * from another edit.
 */
export const ProjectPatchSchema = ProjectCreateSchema.partial()
    .extend({ archived: z.boolean().optional() })
    .refine((value) => Object.keys(value).length > 0, { error: 'no changes were supplied' });
/** Editing an organisation. Same shape of rule as `ProjectPatchSchema`. */
export const OrganizationPatchSchema = z
    .strictObject({
    name: z.string().trim().min(1).max(100).optional(),
    slug: z
        .string()
        .trim()
        .min(1)
        .max(64)
        .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'must be lowercase words separated by single hyphens')
        .optional(),
    archived: z.boolean().optional(),
})
    .refine((value) => Object.keys(value).length > 0, { error: 'no changes were supplied' });
export const MEMBER_ROLES = ['staff', 'client'];
/**
 * A client member is pinned to exactly one project; staff see the whole organisation. The
 * refinement is what keeps an invitation from creating a client with access to everything.
 */
export const InvitationSchema = z
    .strictObject({
    email: z.email().max(254),
    role: z.enum(MEMBER_ROLES),
    projectId: Identifier.optional(),
})
    .refine((value) => value.role !== 'client' || value.projectId !== undefined, {
    error: 'a client invitation must name a project',
    path: ['projectId'],
});
/**
 * Editing a membership: their role, or which project a client sees.
 *
 * The same refinement as an invitation, for the same reason — a client with no project sees
 * the whole organisation. Promoting someone to staff clears the pin, because staff are not
 * pinned; demoting to client requires one to be named.
 */
export const MemberPatchSchema = z
    .strictObject({
    role: z.enum(MEMBER_ROLES).optional(),
    projectId: Identifier.optional(),
    /**
     * Whether this person is a preferred notification recipient in this organisation.
     *
     * Independent of `role` and `projectId`, which is why the `client must be pinned` refinement
     * below tests `value.role` rather than "anything was sent": a notifier-only patch supplies
     * no role at all and must not be refused for failing to pin a project it was not moving.
     */
    notifier: z.boolean().optional(),
})
    .refine((value) => Object.keys(value).length > 0, { error: 'no changes were supplied' })
    .refine((value) => value.role !== 'client' || value.projectId !== undefined, {
    error: 'a client member must be pinned to a project',
    path: ['projectId'],
});
/** Response shapes, so the SPA and the API cannot disagree about what a card carries. */
export const IssueSummarySchema = z.strictObject({
    id: Identifier,
    number: z.number().int(),
    /**
     * WHICH PROJECT THIS BELONGS TO. On the summary because search crosses projects: the board
     * shows one project at a time and never has to ask, but a result list drawn from everything
     * the viewer can reach must be able to say where each row came from, and two projects in one
     * organization can hold issues with the same title.
     *
     * The id, not the name. The SPA already holds the viewer's projects and resolves the name
     * from this; sending the name too would put a second copy on the wire that goes stale the
     * moment a project is renamed.
     */
    projectId: Identifier,
    title: z.string(),
    bucket: z.enum(BOARD_BUCKET_IDS),
    kind: z.enum(['maintenance', 'feature', 'needs_triage']),
    approvalStatus: z.enum(APPROVAL_STATUSES),
    imported: z.boolean(),
    /**
     * Open or closed on GitHub. Carried on the summary because the approval gate reads it —
     * an imported feature is gated only while it is unfinished — and a card that cannot answer
     * that question would have to infer it from the bucket, which is derived from this.
     */
    state: z.enum(['open', 'closed']),
    size: z.enum(TSHIRT_SIZES).nullable(),
    estimatedHours: z.number().nullable(),
    actualHours: z.number().nullable(),
    noCharge: z.boolean(),
    htmlUrl: z.string(),
    assigneeLogin: z.string().nullable(),
    /**
     * When a staff member marked this as started in Portal, or null. Migration 0020.
     *
     * The SECOND of the two In-progress signals, and it crosses the wire beside the first rather
     * than being folded into it, because the move panel has to tell them apart: taking this off is
     * a write to a Portal column, taking the login off is a write to somebody's GitHub issue with
     * an entry in its timeline. A single derived "started" boolean could not say which.
     */
    startedAt: z.string().nullable(),
    /**
     * Who submitted it through the portal, by name. Null for anything mirrored in from a
     * repository, which is most of a board.
     *
     * On the SUMMARY rather than only on the detail, because the question this answers — "did
     * they fix my feedback yet?" — is asked of a list. `assigneeLogin` above is a GitHub login
     * and cannot stand in for it: an issue opened through the portal is opened by the App.
     */
    submittedByName: z.string().nullable(),
    /**
     * When staff raised a question against this issue, or null. THE RAW TIMESTAMP — not a
     * derived boolean and not a derived reason.
     *
     * A boolean would leave the card unable to tell a staff question from a pending decision.
     * A reason would move the derivation into the serializer, where it would be a second
     * implementation of `deriveAttention` — which is precisely the bucketExpr/deriveBucket
     * mistake this codebase already writes contracts to prevent. Both sides call the one shared
     * function over this field.
     */
    attentionRequestedAt: z.string().nullable(),
    /**
     * When the client answered a standing question, or null. Migration 0023.
     *
     * MUTUALLY EXCLUSIVE WITH THE FIELD ABOVE by construction, not by convention: the client's
     * reply clears one and stamps the other in a single UPDATE, and raising a fresh question
     * clears this. A card can therefore never claim to be both waiting and answered.
     *
     * Raw, like `attentionRequestedAt` and for the same reason: `deriveAnswered` in
     * @portal/shared is the one implementation of the closed-suppression rule, and both sides
     * call it over this field rather than the serializer shipping a boolean somebody would
     * eventually compute a second way.
     */
    attentionAnsweredAt: z.string().nullable(),
    /**
     * The `info-needed` GitHub label is on this issue and the issue is still open.
     *
     * A DERIVED BOOLEAN here, unlike `attentionRequestedAt` above, and the asymmetry is
     * deliberate. That field is raw because two different marks are computed from it and the
     * card has to tell them apart. This one has a single meaning, and shipping the whole label
     * array to compute it would put every label a client's repository happens to carry —
     * including the `portal:` ones this product writes for its own bookkeeping — onto a
     * client-facing payload to answer one yes/no question. `needsInformation` in @portal/shared
     * is the one implementation; the serializer calls it and nothing else does.
     */
    infoNeeded: z.boolean(),
    /**
     * When GitHub closed it, or null while it is open. THE DELIVERY DATE, and it is on the
     * summary because the History table is built from summaries and has a Delivered column.
     *
     * It used to substitute `updatedAt` for this, on the reasoning that everything in the
     * Completed bucket is delivered so its last update is close enough. It is not close enough:
     * `updated_at` moves for portal-side reasons that have nothing to do with delivery — a
     * sizing edit, a no-charge toggle, a one-off hours backfill — and every row touched then
     * reports today as the day it shipped. A one-time sweep over 725 closed issues did exactly
     * that and restamped the whole History page.
     */
    closedAt: z.string().nullable(),
    /** Original GitHub creation timestamp; portal creation is the fallback for legacy rows. */
    submittedAt: z.string(),
    updatedAt: z.string(),
});
/**
 * WHO ASKED FOR THIS, in full, on the issue detail.
 *
 * A response shape, not an input one: it is assembled server-side from the session, and — for
 * a submission forwarded from another product — from that product's own request. Field names
 * match the columns behind them one for one, so nothing has to be remapped on the way through.
 *
 * The origin fields name a partner and an organization inside a CLIENT's system. They are
 * plain strings and Portal resolves them to nothing: Modero is one Portal organization, and
 * its partners are not Portal organizations. Never key an authorisation decision on them.
 *
 * Always present, never null, and usually all-null: every field is independently unknown. An
 * issue mirrored in from a repository was not submitted through the portal by anybody, and a
 * submission forwarded from a partner has an origin but no portal user.
 */
export const IssueAttributionSchema = z.strictObject({
    submittedByUserId: Identifier.nullable(),
    /** Their name at submission time. Outlives the account, so it is never re-derived. */
    submittedByName: z.string().nullable(),
    originPartnerId: z.string().nullable(),
    originPartnerName: z.string().nullable(),
    originOrgId: z.string().nullable(),
    originOrgName: z.string().nullable(),
    /** The submitter's own category for the feedback. Their vocabulary, not ours. */
    feedbackCategory: z.string().nullable(),
});
/**
 * BEHIND-THE-SCENES CONTEXT ON A FEEDBACK SUBMISSION — AN ALLOW-LIST, NOT A CAPTURE.
 *
 * Five fields, and a strict object, so this is enforced by the schema rather than by whoever
 * writes the next version of the form: anything not named here is a 400, not a silently
 * ignored key.
 *
 * WHY SO NARROW. What a client submits ends up in an issue body that Portal staff read across
 * every organisation. The obvious richer capture — console lines, network entries, breadcrumb
 * text scraped from the DOM, the title of the modal the user was in — carries whatever
 * happened to be on their screen, and for a client whose product is white-labelled that is
 * their own partners' and customers' names. Redaction does not help: a redactor removes
 * tokens and email addresses it recognises and passes everything else through, so the failure
 * mode is a name nobody thought to pattern-match appearing in another client's issue.
 *
 * So nothing here is scraped. Every field is either a number the browser reports about itself
 * or a string a Portal developer wrote.
 */
export const FeedbackTelemetrySchema = z.strictObject({
    /**
     * The router's PATTERN — `/issues/:id` — and never the URL. A URL carries the query string
     * and whatever the path segments were filled in with, which on this app means another
     * tenant's row ids in a body staff across every organisation can read.
     *
     * A leading slash alone does NOT reject an absolute URL: `//evil.example/x` is
     * protocol-relative, starts with a slash, and is absolute. The second character has to be
     * excluded explicitly, which is what the negative lookahead is for.
     */
    routePattern: z
        .string()
        .trim()
        .min(1)
        .max(200)
        .regex(/^\/(?!\/)[A-Za-z0-9\-_/:.]*$/, 'must be a route pattern, not a URL'),
    /**
     * Which piece of the UI the form was opened from, in a vocabulary a developer chose and
     * wrote into the call site: `issue-detail`, `board-header`. Not a heading read off the page.
     */
    surface: z
        .string()
        .trim()
        .min(1)
        .max(64)
        .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'must be lowercase words separated by single hyphens'),
    /** The SPA build the report came from. Half of "it works on my machine". */
    appVersion: z.string().trim().min(1).max(64),
    userAgent: z.string().trim().min(1).max(512),
    viewport: z.strictObject({
        width: z.number().int().min(0).max(100_000),
        height: z.number().int().min(0).max(100_000),
    }),
});
/**
 * A feedback submission — about the Projects Portal itself, and nothing else.
 *
 * IT NAMES NO DESTINATION, DELIBERATELY. This form is for reporting problems with and
 * requesting improvements to the portal, so there is exactly one place its issues can land:
 * the Portal's own project, read from deployment configuration in the handler. Work on a
 * client's own project is what the New request form is for, and it carries the approval and
 * estimate flow that this deliberately does not.
 *
 * An earlier version offered a choice between the two and asked the reader to make it. That put
 * a question in front of somebody who had come to report a bug, and attached a paragraph about
 * approvals and estimates to a form where neither applies.
 *
 * There is no `title` either: the GitHub issue's title is composed server-side from the first
 * line of the message. Letting the client write it would put client-chosen wording into the
 * highest weighted field the classifier reads.
 */
export const FeedbackSubmissionSchema = z.strictObject({
    /**
     * Their word for what this is: "something is broken", "an idea", whatever the form offers.
     * Free text on purpose — the column behind it has no CHECK, because a fixed enum here would
     * mean a migration every time the form gained an option.
     *
     * It is stored, and it is deliberately kept OUT of the GitHub issue's title and body. See
     * `feedbackTitle` in apps/api/src/routes/feedback.ts for what that would otherwise cost.
     */
    category: z.string().trim().min(1).max(64).optional(),
    /** Bounded exactly like an issue body, because it becomes one. */
    message: z.string().trim().min(1).max(20000),
    telemetry: FeedbackTelemetrySchema.optional(),
});
/**
 * THE ATTACHMENT CEILING, DECLARED ONCE.
 *
 * It lived only in the intake form, where it was a courtesy that saves a doomed round trip
 * and never a control. It is here so the API — which is where it is actually enforced, over
 * bytes that arrived rather than over a header — and the SPA read the same number. Restating
 * a bound in two places is how the two come to disagree about it, which is the reasoning
 * `routes/parse.ts` already gives for composing its schemas from `.shape.x`.
 *
 * MiB, not MB: the number a client sees is "250 MB" and the number enforced is 250 × 1024².
 *
 * WHY THIS NUMBER IS NOW A PRODUCT DECISION RATHER THAN A MEMORY BOUND. It was 10 MiB from the
 * first commit, with no reason recorded, and it was quietly doing two jobs: bounding what a
 * client may send AND bounding what the API holds in memory, because the upload path buffered
 * the whole body before it could measure it. The second job is gone — the route streams the
 * body through a counting transform into a multipart R2 upload, so the resident cost of an
 * upload is a few parts rather than the file — and the number is free to be about clients.
 *
 * What it is NOT free of:
 *
 *  - The API hostname is DNS-only at Cloudflare. If that record is ever proxied, the plan's
 *    request-body limit (100 MB on Free and Pro) becomes a hard wall in front of this number,
 *    and an upload over it is refused by the edge with an error this application never sees.
 *  - A download streams back through the API and is billed as Railway egress.
 *  - Nothing deletes objects yet, so an orphaned upload is now a quarter of a gigabyte.
 *
 * The MCP tools do NOT inherit it. Bytes cross a JSON-RPC message as base64, so those bounds
 * are their own and much smaller — see `MCP_UPLOAD_CEILING_BYTES` and `MCP_FILE_CEILING_BYTES`.
 */
export const MAX_ATTACHMENT_BYTES = 250 * 1024 * 1024;
/**
 * WHAT THE PORTAL WILL SERVE BACK UNDER ITS OWN NAME — not a list of what may be uploaded.
 *
 * ANY FILE MAY BE ATTACHED. A client answering "can you show me?" turns up with a `.docx`, a
 * `.zip` of logs, a `.har`, a CAD export, whatever their problem is made of, and an upload
 * refused on type is a client who cannot report their issue. So the upload path admits the
 * bytes whatever they are, and the type question moved to where it belongs: what
 * `Content-Type` the download path is willing to put on them.
 *
 * A type on THIS list is served as itself, and only after the bytes were SNIFFED as exactly
 * that type — see `storedMediaType` in apps/api/src/storage/sniff.ts. Everything else is
 * served as `OPAQUE_MEDIA_TYPE`, which a browser saves rather than renders.
 *
 * That is why the list is short and why the absences are the same ones as before:
 *
 *  - `image/svg+xml`. An SVG is a scriptable document wearing an image's name. It may now be
 *    uploaded, and it comes back as opaque bytes with a download disposition — never as a
 *    type a browser will parse as markup on this origin.
 *  - `text/html`, for the same reason with none of the disguise.
 *  - `application/zip` and the Office formats — containers whose contents nothing here can
 *    see. Storable, downloadable, never labelled back as something a browser opens.
 *
 * ADDING TO THIS LIST IS A SECURITY DECISION, not a convenience one: it means the portal will
 * hand a browser that type on its own origin. Adding to what may be UPLOADED is no longer a
 * decision at all, because there is nothing left to add.
 */
export const SERVABLE_MEDIA_TYPES = [
    'image/png',
    'image/jpeg',
    'image/gif',
    'image/webp',
    'application/pdf',
    'text/plain',
];
export const isServableMediaType = (value) => SERVABLE_MEDIA_TYPES.includes(value);
/**
 * WHAT EVERYTHING ELSE IS SERVED AS — and stored as, when the bytes and the declaration
 * disagree about what they are.
 *
 * `application/octet-stream` is not a failure value. It is the honest answer for bytes this
 * application has not identified: the browser saves the file under the name it came with, the
 * operating system opens it by extension, and nothing on this origin ever parses it.
 */
export const OPAQUE_MEDIA_TYPE = 'application/octet-stream';
/**
 * One stored attachment, as the list route and the upload response return it.
 *
 * `storageKey` is deliberately absent. It is an internal identifier, the browser has no use
 * for one, and the bytes are fetched by attachment id through a route that authorises on
 * every read.
 */
export const AttachmentSchema = z.strictObject({
    id: Identifier,
    filename: z.string(),
    bytes: z.number().int(),
    contentType: z.string(),
    uploadedAt: z.string(),
    /** The durable name copy, so "Priya attached this" survives Priya's account being closed. */
    uploadedByName: z.string().nullable(),
});
export const ApiErrorSchema = z.strictObject({
    error: z.string().max(200),
    detail: z.string().max(2000).optional(),
});
/** Validate a request body. Returns a typed result; throws for nothing. */
export function parseApiInput(schema, input) {
    return toSchemaResult(schema.safeParse(input));
}
/**
 * A change to a tenant's reminder settings.
 *
 * EVERY FIELD IS OPTIONAL AND EVERY FIELD IS NULLABLE, and the two mean different things —
 * which is the whole reason this schema is written out rather than derived. Omitting a key
 * leaves that setting alone; sending `null` resets it to inherit from the level above.
 * Collapsing them would make a request that changes one rung silently clear every other
 * setting, so an operator adjusting the second nudge would quietly switch off the quiet hours.
 *
 * IT VALIDATES WHAT THE DATABASE WOULD ONLY REFUSE AFTER THE FACT. 0016's CHECK constraints
 * are the backstop and they raise 23514 — a bare "violates check constraint" with a constraint
 * name in it, which is not a sentence anybody can act on. The refusals here name the field and
 * say what would have happened.
 */
/**
 * Does this name a zone `Intl` can actually resolve?
 *
 * A TYPO HERE STOPS A TENANT'S REMINDERS WITH NO ERROR ANYWHERE A HUMAN LOOKS. The sweep calls
 * `localHourAndDate` for every candidate, `Intl.DateTimeFormat` throws `RangeError` on an
 * unknown zone, and the throw is caught by the per-organisation guard in `apps/cron` — which
 * logs one line, counts a failure, and moves on to the next tenant. Every reminder for that
 * client stops, indefinitely, and the run still ends. `America/Chigago` is a support ticket
 * three weeks later about reminders that "never started".
 *
 * A round-trip through the constructor rather than a list: `Intl.supportedValuesOf` is the same
 * answer with a version-dependent set behind it, and this asks the exact API the sweep uses.
 */
const resolvesAsTimeZone = (zone) => {
    try {
        new Intl.DateTimeFormat('en-CA', { timeZone: zone });
        return true;
    }
    catch {
        return false;
    }
};
export const ReminderPolicyPatchSchema = z
    .strictObject({
    /** NULL inherits. At the organisation level, inheriting resolves to off. */
    enabled: z.boolean().nullable().optional(),
    /** An IANA zone name. Resolved against the PROJECT, not the reader — see the column note. */
    timezone: z
        .string()
        .min(1)
        .max(64)
        .refine(resolvesAsTimeZone, { message: 'not a recognised time zone name' })
        .nullable()
        .optional(),
    quietStartHour: z.number().int().min(0).max(23).nullable().optional(),
    quietEndHour: z.number().int().min(0).max(23).nullable().optional(),
    holidays: z
        .array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'each holiday must be an ISO date'))
        .max(365)
        .nullable()
        .optional(),
    mutedUntil: z.string().datetime().nullable().optional(),
    /**
     * Project rows only. `true` declines the client's mute; `null` hands the decision back.
     *
     * Deliberately NOT refined against `projectId` here — the route strips `projectId` from the
     * body before parsing, so the two are never in scope together. The route makes the refusal,
     * with a sentence; 0022's CHECK is the backstop underneath it.
     */
    muteOptOut: z.boolean().nullable().optional(),
    clientRung1Days: z.number().int().min(0).max(365).nullable().optional(),
    clientRung2Days: z.number().int().min(0).max(365).nullable().optional(),
    clientRung3Days: z.number().int().min(0).max(365).nullable().optional(),
    staffRung1Days: z.number().int().min(0).max(365).nullable().optional(),
    staffRung2Days: z.number().int().min(0).max(365).nullable().optional(),
    staffRung3Days: z.number().int().min(0).max(365).nullable().optional(),
})
    .refine((patch) => 
// Both or neither. Half a window is not a window, and the resolver would have to invent
// the other half — which is how a tenant ends up quiet at every hour of every day.
(patch.quietStartHour === undefined) === (patch.quietEndHour === undefined) ||
    // Unless neither is being changed at all.
    (patch.quietStartHour === undefined && patch.quietEndHour === undefined), { message: 'set both quiet hours or neither', path: ['quietStartHour'] })
    .refine((patch) => patch.quietStartHour === null ||
    patch.quietEndHour === null ||
    patch.quietStartHour === undefined ||
    patch.quietEndHour === undefined ||
    patch.quietStartHour !== patch.quietEndHour, {
    // Equal bounds read as "always quiet" and "never quiet" with equal justification, and
    // whichever the code picks, the operator meant the other one. 0016 refuses them too.
    message: 'the quiet window cannot start and end at the same hour',
    path: ['quietEndHour'],
});
/**
 * A submission forwarded from a client's own product — Modero's Vigilis is the first.
 *
 * PERMISSIVE, NOT STRICT, and deliberately so. This crosses a boundary Portal does not control:
 * the caller is somebody else's codebase, shipped on somebody else's schedule, and a strict
 * object turns every field they add into a 400 and a support conversation. Unknown keys are
 * dropped. The repository's own rule says the same — keep schemas permissive at persisted-data
 * boundaries.
 *
 * NOTHING HERE NAMES A DESTINATION. There is no project, organisation or board field, and there
 * must never be one: the credential in the header resolves the project, so no value a caller
 * controls can move a submission onto another client's board. This is the same reasoning the
 * Portal feedback route sets out at length, applied to an inbound caller instead of a session.
 *
 * THE ORIGIN FIELDS ARE STRINGS AND STAY STRINGS. They name a partner and an organisation
 * inside the CLIENT's system — Vigilis runs platform -> partner -> org -> user — and Portal
 * does not model that as its own tenancy. An id here is a label to display, never something to
 * join on, and giving it a foreign key would be the first step to making a client's partners
 * into Portal organisations.
 */
export const ForwardedSubmissionSchema = z.object({
    title: z.string().trim().min(1).max(200),
    body: z.string().trim().max(20000).optional(),
    /** How the submitter categorised it. Their vocabulary, unconstrained — see `feedback_category`. */
    category: z.string().trim().max(80).optional(),
    submitter: z
        .object({
        /**
         * Displayed, and it is all Portal will ever know about this person: they have no Portal
         * account, so `submitted_by_user_id` stays NULL and this is the durable record of who
         * asked. Bounded because it is rendered in Slack, in email and on the board.
         */
        name: z.string().trim().min(1).max(120),
    })
        .optional(),
    origin: z
        .object({
        partnerId: z.string().trim().max(120).optional(),
        partnerName: z.string().trim().max(200).optional(),
        orgId: z.string().trim().max(120).optional(),
        orgName: z.string().trim().max(200).optional(),
    })
        .optional(),
});
/**
 * Every way the collector closes a turn, and the ONE place the list is written: the Zod enum
 * below, the review rules, the worker's AI evidence schema, the db row type and the web types
 * all derive from it. `time_entry_closed_by_check` restates it in SQL (0038 is the latest
 * version) and cannot import it, so a value added here needs a migration in the same commit.
 *
 * `triage_unit` is a turn split by hand: vigilis's `timelog-mark.sh` closes the open turn at a
 * triage unit boundary so each issue's time lands on its own entry. Before 0038 every one was
 * refused with a 400 and never reached the ledger.
 */
export const TIME_ENTRY_CLOSED_BY = [
    'stop',
    'stop_failure',
    'session_end',
    'next_prompt',
    'triage_unit',
];
/**
 * One closed interaction, pushed from a client's local time-tracking spool (vigilis's
 * `.claude/scripts/timelog-*.sh` first). Same credential as `ForwardedSubmissionSchema` above,
 * same reasoning: PERMISSIVE at this boundary, nothing here names a destination, and every
 * bound below is restated from migration 0027's own CHECK constraints so a caller's mistake
 * comes back as a 400 naming the field rather than as a constraint violation with no context.
 *
 * `startedAt`/`endedAt` are the CLIENT's clock. Ingestion can lag the interaction by any
 * amount and billing the wrong minute is worse than trusting a value the caller has nothing to
 * gain by lying about — the same trade `ForwardedSubmissionSchema`'s `origin` fields make.
 */
export const TimeEntrySubmissionSchema = z
    .object({
    githubIssueNumber: z.number().int().positive().optional(),
    submitterName: z.string().trim().min(1).max(120).optional(),
    sourceSessionId: z.string().trim().min(1).max(200),
    startedAt: z.string().datetime(),
    endedAt: z.string().datetime(),
    // A turn lasting longer than a week is a stuck collector, not one interaction. The bound
    // is intentionally generous; its job is to reject corrupt or hostile measurements rather
    // than restate today's five-minute billing rule.
    wallSeconds: z
        .number()
        .int()
        .min(0)
        .max(7 * 24 * 60 * 60),
    /**
     * The human's think time BEFORE this turn: from the previous turn's Stop to this prompt
     * (`timelog-stop.sh`'s header). It sits outside `startedAt..endedAt`, so it is routinely
     * larger than `wallSeconds` — seven minutes of thought, then a twelve-second answer — and
     * is never compared with it. 0030's idle-within-wall rule read it as idle INSIDE the turn
     * and refused hundreds of real entries; 0038 drops it.
     */
    idleSeconds: z
        .number()
        .int()
        .min(0)
        .max(7 * 24 * 60 * 60),
    billableMinutes: z
        .number()
        .int()
        .positive()
        .max(24 * 60),
    /**
     * The fourth value, `next_prompt`, is an ABANDONED turn: one whose Stop hook never fired
     * because the process was interrupted, killed, or errored in a way Stop never sees, closed
     * out by the NEXT prompt arriving rather than by the turn's own end. 0028 added it after this
     * enum's original three (`stop`, `stop_failure`, `session_end`) rejected every one of them —
     * silently under-billing exactly the turns most likely to run long. See 0028's own note.
     * The fifth, `triage_unit`, is documented on `TIME_ENTRY_CLOSED_BY`.
     */
    closedBy: z.enum(TIME_ENTRY_CLOSED_BY),
    promptExcerpt: z.string().trim().max(500).optional(),
    /** Collector-reported provenance is evidence for review, never a billing decision. */
    reportedOrigin: z
        .enum(['human', 'slash_command', 'task_notification', 'system_reminder', 'unknown'])
        .optional(),
    sourceEventName: z.string().trim().min(1).max(120).optional(),
    collectorVersion: z.string().trim().min(1).max(80).optional(),
    /**
     * The dedup key. Unique per organization at the database, so a retried push after a timeout
     * lands as a no-op rather than a second row — see 0027's UNIQUE(organization_id, external_id).
     */
    externalId: z.string().trim().min(1).max(300),
})
    .superRefine((entry, context) => {
    if (Date.parse(entry.endedAt) < Date.parse(entry.startedAt)) {
        context.addIssue({
            code: 'custom',
            path: ['endedAt'],
            message: 'must be on or after startedAt',
        });
    }
});
//# sourceMappingURL=api.js.map